import { useState, useCallback, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { switchLangInPath, catalogPath, localizedPath, type ProductType } from "@/lib/url-slugs";
import { Search, ShoppingCart, User, Menu, X, Shield, Sparkle, Hand, Globe } from "lucide-react";

import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useLanguage, type Lang } from "@/context/LanguageContext";
import { useNavMenuData } from "@/hooks/use-nav-menu-data";
import MegaMenu from "@/components/layout/MegaMenu";
import logo from "@/assets/logo.png";
import SearchDropdown from "@/components/layout/SearchDropdown";
import { useSearchState } from "@/context/SearchContext";

const FLAGS: Record<Lang, { label: string; flag: string; code: string }> = {
  lv: { label: "Latviešu", flag: "🇱🇻", code: "LV" },
  en: { label: "English", flag: "🇬🇧", code: "GB" },
};

const FlagIcon = ({ lang: l }: { lang: Lang }) => {
  const code = l === "lv" ? "lv" : "gb";
  return (
    <img
      src={`https://flagcdn.com/w40/${code}.png`}
      srcSet={`https://flagcdn.com/w80/${code}.png 2x`}
      width="20"
      height="15"
      alt={FLAGS[l].label}
      className="inline-block rounded-sm object-cover"
      style={{ width: 20, height: 15 }}
    />
  );
};


const categoryLinks: { tKey: "nav.rackets" | "nav.apparel" | "nav.shoes" | "nav.bags" | "nav.balls" | "nav.accessories"; type: ProductType; menuKey: string }[] = [
  { tKey: "nav.rackets", type: "rackets", menuKey: "rackets" },
  { tKey: "nav.apparel", type: "apparel", menuKey: "apparel" },
  { tKey: "nav.shoes", type: "shoes", menuKey: "shoes" },
  { tKey: "nav.bags", type: "bags", menuKey: "bags" },
  { tKey: "nav.balls", type: "balls", menuKey: "balls" },
  { tKey: "nav.accessories", type: "accessories", menuKey: "accessories" },
];


const Header = () => {
  const { searchQuery, setSearchQuery, searchFocused, setSearchFocused, floatingSearchActive } = useSearchState();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileAccordion, setMobileAccordion] = useState<string | null>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [activeMenuKey, setActiveMenuKey] = useState<string | null>(null);
  const desktopNavRef = useRef<HTMLDivElement>(null);
  const navItemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { totalItems, setIsCartOpen } = useCart();
  const { user, isAdmin, isStarviePlayer } = useAuth();
  const { t, lang, setLang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const navMenuData = useNavMenuData({ includeBrands: true });

  const scrollToTopMobile = useCallback(() => {
    if (window.matchMedia("(max-width: 767px)").matches) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    }
  }, []);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setActiveMenuKey(null);
  }, []);

  const clearCloseTimeout = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);

  const scheduleCloseMenu = useCallback(() => {
    clearCloseTimeout();
    hoverTimeoutRef.current = setTimeout(() => {
      closeMenu();
      hoverTimeoutRef.current = null;
    }, 250);
  }, [clearCloseTimeout, closeMenu]);

  // Desktop hover is tracked on one shared wrapper so the menu stays open
  // while the pointer is over either the trigger or the dropdown panel.
  const handleNavEnter = useCallback((menuKey: string) => {
    clearCloseTimeout();
    const menuData = navMenuData[menuKey];
    const hasDropdown = Boolean(menuData && (menuData.categories.length > 0 || menuData.brands.length > 0));

    if (!hasDropdown) {
      closeMenu();
      return;
    }

    setActiveMenuKey((currentKey) => (currentKey === menuKey ? currentKey : menuKey));
    setIsOpen(true);
  }, [clearCloseTimeout, closeMenu, navMenuData]);

  const handleDesktopAreaBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    const nextFocused = e.relatedTarget as Node | null;
    if (!nextFocused || !e.currentTarget.contains(nextFocused)) {
      closeMenu();
    }
  }, [closeMenu]);

  // Click should navigate immediately, so we only clear timers and close state.
  const handleCategoryClick = useCallback(() => {
    clearCloseTimeout();
    closeMenu();
  }, [clearCloseTimeout, closeMenu]);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMenu();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, closeMenu]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/${lang}/${lang === "lv" ? "meklesana" : "search"}?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
      setSearchFocused(false);
      setMobileMenuOpen(false);
    }
  };

  const handleSearchSelect = useCallback(() => {
    setSearchQuery("");
    setSearchFocused(false);
    setMobileMenuOpen(false);
  }, []);

  // Mobile dropdown panel: mount → animate open, reverse animation on close.
  const [menuRendered, setMenuRendered] = useState(false);
  const [menuShown, setMenuShown] = useState(false);
  useEffect(() => {
    if (mobileMenuOpen) {
      setMenuRendered(true);
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => setMenuShown(true));
      });
      return () => cancelAnimationFrame(raf);
    }
    setMenuShown(false);
    const t = setTimeout(() => setMenuRendered(false), 280);
    return () => clearTimeout(t);
  }, [mobileMenuOpen]);

  // Close mobile menu on Escape
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileMenuOpen]);

  const activeLink = activeMenuKey
    ? categoryLinks.find((l) => l.menuKey === activeMenuKey)
    : null;
  const activeMenuData = activeMenuKey ? navMenuData[activeMenuKey] : null;
  const showDropdown = Boolean(
    isOpen && activeMenuData && (activeMenuData.categories.length > 0 || activeMenuData.brands.length > 0)
  );

  return (
    <header className="relative md:sticky md:top-0 z-50 bg-white shadow-md">
      {/* Premium hero text above main bar */}
      <Link
        to="/"
        aria-label="Tavs Padel Veikals - Sākums"
        className="block w-full cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary hover:opacity-95 transition-opacity"
        style={{
          background:
            "linear-gradient(to bottom, hsl(150 60% 88%) 0%, hsl(150 55% 92%) 30%, hsl(150 50% 96%) 60%, hsl(0 0% 100%) 100%)",
        }}
      >
        <div className="container mx-auto px-4 py-1.5 flex justify-center animate-fade-in">
          <div className="inline-flex flex-col items-center text-center" style={{ fontFamily: "'Figtree', sans-serif" }}>
            <h2 className="font-extrabold uppercase text-foreground text-[11px] sm:text-xs md:text-sm tracking-[0.18em] leading-tight" style={{ fontFamily: "'Figtree', sans-serif", fontWeight: 800 }}>
              Tavs Padel Veikals
            </h2>
            <p className="text-[8px] sm:text-[9px] md:text-[10px] text-muted-foreground/80 tracking-[0.2em] uppercase leading-tight mt-0.5" style={{ fontFamily: "'Figtree', sans-serif" }}>
              Viss padelam vienuviet
            </p>
          </div>
        </div>
      </Link>
      {/* Mobile header bar */}
      <div className="md:hidden relative container mx-auto flex items-center gap-2 h-16 px-3">
        <Link to="/" className="flex-shrink-0 hover:opacity-90 transition-opacity">
          <img src={logo} alt="Padel Corner" className="h-8 w-auto" width={47} height={32} />
        </Link>

        <form onSubmit={handleSearch} className="flex-1 min-w-[160px]">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              name="q"
              dir="ltr"
              lang="en"
              inputMode="search"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              placeholder="Meklēt produktus"
              aria-label="Meklēt produktus"
              className="w-full h-11 rounded-full bg-muted pl-10 pr-10 text-base text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-primary transition-colors [unicode-bidi:plaintext]"
            />
            {searchQuery.length > 0 && (
              <button
                type="button"
                onClick={() => { setSearchQuery(""); setSearchFocused(false); }}
                aria-label="Notīrīt meklēšanu"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 flex items-center justify-center rounded-full text-muted-foreground hover:bg-background/70"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </form>

        <button
          onClick={() => setMobileMenuOpen((open) => !open)}
          aria-label={mobileMenuOpen ? "Aizvērt izvēlni" : "Izvēlne"}
          aria-expanded={mobileMenuOpen}
          className="relative flex-shrink-0 h-11 w-11 rounded-full bg-primary text-primary-foreground shadow-md flex flex-col items-center justify-center gap-0.5 active:scale-95 transition-transform"
        >
          <span className="relative flex flex-col items-center justify-center gap-0.5">
            <span
              className={`absolute inset-0 flex items-center justify-center transition-all duration-200 motion-reduce:transition-none ${
                mobileMenuOpen ? "opacity-100 rotate-0 scale-100" : "opacity-0 -rotate-90 scale-75"
              }`}
            >
              <X size={20} strokeWidth={2.5} />
            </span>
            <span
              className={`flex flex-col items-center justify-center gap-0.5 transition-all duration-200 motion-reduce:transition-none ${
                mobileMenuOpen ? "opacity-0 rotate-90 scale-75" : "opacity-100 rotate-0 scale-100"
              }`}
            >
              <Menu size={18} strokeWidth={2.5} />
              <Hand size={11} strokeWidth={2.5} className="opacity-90" />
            </span>
          </span>
          {totalItems > 0 && (
            <span className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-[10px] font-bold rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center">
              {totalItems}
            </span>
          )}
        </button>

        <SearchDropdown
          query={searchQuery}
          visible={!floatingSearchActive && searchFocused && searchQuery.trim().length >= 2}
          onSelect={handleSearchSelect}
          onClose={() => setSearchFocused(false)}
          variant="mobile"
        />
      </div>

      {/* Main header bar (desktop) */}
      <div className="hidden md:flex container mx-auto items-center gap-3 h-16 px-4">
        <Link to="/" className="flex-shrink-0 hover:opacity-90 transition-opacity">
          <img src={logo} alt="Padel Corner" className="h-10 md:h-12 w-auto" width={70} height={48} />
        </Link>

        {/* Language Switcher */}
        <div className="relative flex-shrink-0">
          <button
            onClick={() => setLangOpen(!langOpen)}
            className="flex items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-muted transition-colors text-sm"
          >
            <FlagIcon lang={lang} />
            <span className="hidden sm:inline text-xs font-medium text-muted-foreground uppercase">{FLAGS[lang].code}</span>
          </button>
          {langOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setLangOpen(false)} />
              <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-border rounded-lg shadow-lg py-1 min-w-[140px]">
                {(Object.keys(FLAGS) as Lang[]).map((l) => (
                  <button
                    key={l}
                    onClick={() => {
                      setLang(l);
                      setLangOpen(false);
                      const newPath = switchLangInPath(location.pathname, l);
                      navigate(newPath + location.search, { replace: true });
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors ${l === lang ? "bg-muted font-semibold" : ""}`}
                  >
                    <FlagIcon lang={l} />
                    <span>{FLAGS[l].label}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Search bar */}
        <form onSubmit={handleSearch} className="flex-1 max-w-xl">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              type="search"
              name="q"
              dir="ltr"
              lang="en"
              inputMode="search"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              placeholder={t("search.placeholder")}
              className="w-full h-10 rounded-lg bg-muted pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-primary transition-colors [unicode-bidi:plaintext]"
            />
            <SearchDropdown
              query={searchQuery}
              visible={searchFocused && searchQuery.trim().length >= 2}
              onSelect={handleSearchSelect}
              onClose={() => setSearchFocused(false)}
            />
          </div>
        </form>

        {/* Action icons */}
        <div className="flex items-center gap-0.5 ml-auto">
          {isAdmin && (
            <Link to="/admin/dashboard" className="p-2.5 hover:bg-muted rounded-lg transition-colors" title="Admin Panel">
              <Shield size={22} className="text-primary" />
            </Link>
          )}
          {(isStarviePlayer || isAdmin) && (
            <Link to="/starvie-player" className="p-2.5 hover:bg-muted rounded-lg transition-colors" title="Starvie Player Panel" aria-label="Starvie Player Panel">
              <Sparkle size={22} className="text-primary" strokeWidth={2} />
            </Link>
          )}
          <Link to={user ? localizedPath(lang, "account") : localizedPath(lang, "auth")} className="p-2.5 hover:bg-muted rounded-lg transition-colors" aria-label="Account">
            <User size={22} className="text-primary" />
          </Link>
          <button onClick={() => setIsCartOpen(true)} className="relative p-2.5 hover:bg-muted rounded-lg transition-colors" aria-label="Shopping cart">
            <ShoppingCart size={22} className="text-primary" />
            {totalItems > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-accent text-accent-foreground text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {totalItems}
              </span>
            )}
          </button>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2.5 md:hidden hover:bg-muted rounded-lg transition-colors">
            {mobileMenuOpen ? <X size={20} className="text-foreground" /> : <Menu size={20} className="text-foreground" />}
          </button>
        </div>
      </div>

      {/* Desktop nav uses one shared hover area for triggers + dropdown */}
      <div
        className="hidden md:block relative"
        ref={desktopNavRef}
        onMouseEnter={clearCloseTimeout}
        onMouseLeave={scheduleCloseMenu}
        onFocusCapture={clearCloseTimeout}
        onBlur={handleDesktopAreaBlur}
      >
        <nav className="bg-primary" role="menubar">
          <div className="container mx-auto px-4 flex items-center justify-center gap-1">
            {categoryLinks.map((link) => {
              const isActive = isOpen && activeMenuKey === link.menuKey;
              const menuData = navMenuData[link.menuKey];
              const hasDropdown = Boolean(menuData && (menuData.categories.length > 0 || menuData.brands.length > 0));

              return (
                <div
                  key={catalogPath(lang, link.type)}
                  className="relative"
                  role="none"
                  ref={(el) => { navItemRefs.current[link.menuKey] = el; }}
                  onMouseEnter={() => handleNavEnter(link.menuKey)}
                >
                  <Link
                    to={catalogPath(lang, link.type)}
                    role="menuitem"
                    aria-haspopup={hasDropdown ? "menu" : undefined}
                    aria-expanded={hasDropdown ? isActive : undefined}
                    className={`block px-4 py-2.5 text-sm font-medium transition-colors rounded ${
                      isActive
                        ? "text-primary-foreground bg-primary-foreground/15"
                        : "text-primary-foreground/85 hover:text-primary-foreground hover:bg-primary-foreground/10"
                    }`}
                    onFocus={() => handleNavEnter(link.menuKey)}
                    onClick={handleCategoryClick}
                  >
                    {t(link.tKey)}
                  </Link>
              </div>
            );
          })}
        </div>
        </nav>

        {showDropdown && activeLink && activeMenuData && (
          <div
            className="absolute z-50"
            style={{
              top: "100%",
              left: (() => {
                const el = activeMenuKey ? navItemRefs.current[activeMenuKey] : null;
                if (el) return el.offsetLeft;
                return 0;
              })(),
            }}
          >
            <div className="h-2" />
            <div className="bg-white border border-border rounded-xl shadow-xl overflow-hidden animate-fade-in" style={{ animationDuration: "150ms" }}>
              <MegaMenu groupKey={activeLink.menuKey} data={activeMenuData} onClose={closeMenu} />
            </div>
          </div>
        )}
      </div>

      {/* Mobile menu — anchored liquid-glass dropdown unfolding from the green button */}
      {menuRendered && (
        <div className="md:hidden">
          {/* Outside tap closes */}
          <div
            className={`fixed inset-0 z-40 transition-opacity duration-200 ${menuShown ? "opacity-100" : "opacity-0"}`}
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />
          <div
            role="menu"
            aria-label="Izvēlne"
            className={`absolute z-50 right-3 top-[calc(100%+6px)] w-[min(340px,calc(100vw-24px))] origin-top-right transition-all duration-[260ms] ease-out motion-reduce:transition-none motion-reduce:transform-none ${
              menuShown
                ? "opacity-100 scale-100 translate-y-0"
                : "opacity-0 scale-[0.92] -translate-y-2 pointer-events-none"
            }`}
          >
            <div className="overflow-hidden rounded-2xl border border-white/60 bg-white/90 supports-[backdrop-filter]:bg-white/80 backdrop-blur-xl shadow-[0_16px_48px_-10px_rgba(15,107,58,0.35),0_2px_8px_rgba(15,107,58,0.12)] ring-1 ring-primary/10">
              {/* Small connector toward the button */}
              <div className="absolute -top-1.5 right-[18px] h-3 w-3 rotate-45 border-l border-t border-white/60 bg-white/90 supports-[backdrop-filter]:bg-white/80" />
              <div className="max-h-[70vh] overflow-y-auto overscroll-contain p-3">
                {/* Utility actions */}
                <div className="grid grid-cols-2 gap-2">
                  <Link
                    to={user ? localizedPath(lang, "account") : localizedPath(lang, "auth")}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-2 min-h-[48px] px-3 rounded-xl border border-white/60 bg-white/60 hover:bg-white/90 active:bg-primary/10 transition-colors text-sm font-medium shadow-sm"
                  >
                    <User size={20} className="text-primary" />
                    {user ? "Profils" : "Ielogoties"}
                  </Link>

                  <button
                    onClick={() => { setMobileMenuOpen(false); setIsCartOpen(true); }}
                    className="relative flex items-center gap-2 min-h-[48px] px-3 rounded-xl border border-white/60 bg-white/60 hover:bg-white/90 active:bg-primary/10 transition-colors text-sm font-medium shadow-sm"
                  >
                    <ShoppingCart size={20} className="text-primary" />
                    Grozs
                    {totalItems > 0 && (
                      <span className="ml-auto bg-accent text-accent-foreground text-[11px] font-bold rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center">
                        {totalItems}
                      </span>
                    )}
                  </button>

                  {isAdmin && (
                    <Link
                      to="/admin/dashboard"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 min-h-[48px] px-3 rounded-xl border border-white/60 bg-white/60 hover:bg-white/90 active:bg-primary/10 transition-colors text-sm font-medium shadow-sm"
                    >
                      <Shield size={20} className="text-primary" />
                      Admin Panel
                    </Link>
                  )}

                  {(isStarviePlayer || isAdmin) && (
                    <Link
                      to="/starvie-player"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 min-h-[48px] px-3 rounded-xl border border-white/60 bg-white/60 hover:bg-white/90 active:bg-primary/10 transition-colors text-sm font-medium shadow-sm"
                    >
                      <Sparkle size={20} className="text-primary" strokeWidth={2} />
                      Starvie Player
                    </Link>
                  )}
                </div>

                {/* Language */}
                <div className="pt-3">
                  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                    <Globe size={14} /> Valoda / Valsts
                  </p>
                  <div className="flex gap-2">
                    {(Object.keys(FLAGS) as Lang[]).map((l) => (
                      <button
                        key={l}
                        onClick={() => {
                          setLang(l);
                          setMobileMenuOpen(false);
                          const newPath = switchLangInPath(location.pathname, l);
                          navigate(newPath + location.search, { replace: true });
                        }}
                        className={`flex-1 flex items-center justify-center gap-2 min-h-[44px] rounded-xl border text-sm transition-colors shadow-sm ${
                          l === lang
                            ? "border-primary/50 bg-primary/10 font-semibold text-primary"
                            : "border-white/60 bg-white/60 hover:bg-white/90 active:bg-primary/10"
                        }`}
                      >
                        <FlagIcon lang={l} />
                        <span>{FLAGS[l].label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Categories */}
                <nav className="mt-3 border-t border-primary/10 pt-2 space-y-1">
                  {categoryLinks.map((link) => {
                    const menuData = navMenuData[link.menuKey];
                    const hasDropdown = menuData && (menuData.categories.length > 0 || menuData.brands.length > 0);
                    const isExpanded = mobileAccordion === link.tKey;

                    return (
                      <div key={catalogPath(lang, link.type)}>
                        <button
                          onClick={() => {
                            if (hasDropdown && !isExpanded) {
                              // First tap: expand brands/categories
                              setMobileAccordion(link.tKey);
                            } else {
                              // Second tap (or no dropdown): go to the whole category
                              setMobileAccordion(null);
                              navigate(catalogPath(lang, link.type));
                              setMobileMenuOpen(false);
                              scrollToTopMobile();
                            }
                          }}
                          aria-expanded={hasDropdown ? isExpanded : undefined}
                          className={`w-full flex items-center justify-between min-h-[44px] py-2.5 px-3 text-sm font-medium rounded-xl transition-colors active:bg-primary/10 ${
                            isExpanded
                              ? "text-primary bg-primary/10"
                              : "text-foreground/80 hover:text-primary hover:bg-white/70"
                          }`}
                        >
                          {t(link.tKey)}
                        </button>

                        {hasDropdown && (
                          <div
                            className={`overflow-hidden transition-all duration-200 ${
                              isExpanded ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
                            }`}
                          >
                            <div className="pl-4 pb-2">
                              <MegaMenu
                                groupKey={link.menuKey}
                                data={menuData}
                                onClose={() => { setMobileMenuOpen(false); scrollToTopMobile(); }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
